import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { BoltIcon, PlusIcon } from "@heroicons/react/20/solid";
import NavBar from "../../components/feature/NavBar";
import Footer from "../../components/feature/Footer";
import VideosCollectionView from "../../components/cms/VideosCollectionView";
import EntitiesCollectionView from "../../components/cms/EntitiesCollectionView";
import SubmissionsView from "../../components/cms/SubmissionsView";
import TaxonomiesView from "../../components/cms/TaxonomiesView";
import MaintenanceView from "../../components/cms/MaintenanceView";
import { primaryButton, secondaryButton } from "../../components/admin/ui";
import { usePageMeta } from "../../hooks/usePageMeta";

type Tab = "videos" | "companies" | "agencies" | "submissions" | "taxonomies" | "maintenance";

const TABS: { key: Tab; label: string }[] = [
  { key: "videos", label: "Vidéos" },
  { key: "companies", label: "Marques" },
  { key: "agencies", label: "Studios" },
  { key: "submissions", label: "Soumissions" },
  { key: "taxonomies", label: "Catégories" },
  { key: "maintenance", label: "Maintenance" },
];

export default function CMS() {
  usePageMeta("CMS · Admin");
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (TABS.find((t) => t.key === searchParams.get("tab"))?.key ?? "videos") as Tab;
  const [entityFilter, setEntityFilter] = useState<{ id: string; name: string } | null>(null);
  const [pendingSubmissions, setPendingSubmissions] = useState<number | null>(null);

  const goTo = (tab: Tab) => {
    const next = new URLSearchParams(searchParams);
    if (tab === "videos") next.delete("tab");
    else next.set("tab", tab);
    setSearchParams(next);
  };

  return (
    <div className="min-h-screen bg-neutral-50 md:px-20 px-4">
      <NavBar />

      <div className="max-w-[100vw] mx-auto px-0 md:px-4 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <h1 className="font-bold text-5xl">CMS</h1>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/video/new" className={secondaryButton}>
              <PlusIcon className="w-4 h-4" /> Nouvelle vidéo
            </Link>
            <Link to="/admin/quick-add" className={primaryButton}>
              <BoltIcon className="w-4 h-4" /> Quick Add
            </Link>
          </div>
        </div>

        <nav className="flex gap-1 border-b border-neutral-200 mb-6 overflow-x-auto" aria-label="Sections du CMS">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => goTo(tab.key)}
              aria-current={activeTab === tab.key ? "page" : undefined}
              className={`px-4 py-2.5 text-sm whitespace-nowrap border-b-2 -mb-px transition-colors ${
                activeTab === tab.key
                  ? "border-neutral-950 text-neutral-950 font-semibold"
                  : "border-transparent text-neutral-600 hover:text-neutral-900"
              }`}
            >
              {tab.label}
              {tab.key === "submissions" && !!pendingSubmissions && (
                <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-neutral-950 text-white text-xs tabular-nums">
                  {pendingSubmissions}
                </span>
              )}
            </button>
          ))}
        </nav>

        {activeTab === "videos" && (
          <VideosCollectionView entityFilter={entityFilter} onClearEntityFilter={() => setEntityFilter(null)} />
        )}
        {(activeTab === "companies" || activeTab === "agencies") && (
          <EntitiesCollectionView
            key={activeTab}
            kind={activeTab}
            onShowVideos={(entity) => {
              setEntityFilter(entity);
              goTo("videos");
            }}
          />
        )}
        {activeTab === "submissions" && <SubmissionsView onCountChange={setPendingSubmissions} />}
        {activeTab === "taxonomies" && <TaxonomiesView />}
        {activeTab === "maintenance" && <MaintenanceView />}
      </div>

      <Footer />
    </div>
  );
}
